import * as React from "react";
import { useEffect, useState } from "react";
import HeaderBar from "../commons/components/HeaderBar";
import ReportList, { ReportListProps } from "../reports/ReportList";
import ContactInfoActivity from "../commons/model/activity/ContactInfoActivity";
import { ActivityTimelineActions } from "../patient-management/ActivityTimelineActions";
import AssessmentResultPreview, { AssessmentResultPreviewProps } from "../assessment-result/AssessmentResultPreview";
import ActivityTimeline from "./ActivityTimeline";
import PatientLinks, { LinkServiceName, ServiceLink } from "./PatientLinks";
import { PatientManagementInfo, Tag, TagActions } from "../commons/model/patient/PatientInfo";
import TitledPanel from "../commons/components/TitledPanel";
import "./PatientManagement.scss";
import PatientCard from "./PatientCard";
import StartVideoConsultation from "./StartVideoConsultation";
import { getPatientLabelledData } from "./PatientData";
import AssignmentPanel from "./AssignmentPanel";
import Practice from "../commons/model/Practice";
import Doctor from "../commons/model/Doctor";
import Panel from "../commons/components/Panel";
import AboutPanel from "./AboutPanel";
import { createRoot } from "react-dom/client";

interface ReportsPage extends ReportListProps {
  latestAssessmentResult: AssessmentResultPreviewProps;
}

interface PatientManagementProps {
  patient: PatientManagementInfo;
  reports: ReportsPage;
  breadcrumbLink: string;
  activities: ContactInfoActivity[];
  activityActions: ActivityTimelineActions;
  videoConsultationURL?: string;
  patientExternalLinks: ServiceLink[];
  patientFullDetailsURL: string;
  accountCreationInvitationURL?: string;
  availableTags: Tag[];
  canEditDoctorAssignment: boolean;
  canEditPracticeAssignment: boolean;
  assignableDoctors: Doctor[];
  assignablePractices: Practice[];
  assignmentUrl: string;
  convertToPatientURL?: string;
  convertedPatientProfileURL?: string;
  showLeadStatus: boolean;
  tagActions: TagActions;
}

export default function PatientManagement(props: PatientManagementProps) {
  const [patientLinks, setPatientLinks] = useState([]);

  useEffect(() => {
    setPatientLinks(
      props.patientExternalLinks.filter((link) => link.service !== LinkServiceName.SCREENING)
    );
  }, []);

  return (
    <>
      <HeaderBar
        title={props.patient.patientInformation.fullName}
        breadcrumb={{
          title: props.breadcrumbLink.includes("/archived")
            ? i18n["Page.ArchivedPatients.BackLink"]
            : i18n["Page.Patients.BackLink"],
          link: props.breadcrumbLink,
        }}
        action={
          props.videoConsultationURL && (
            <StartVideoConsultation videoConsultationURL={props.videoConsultationURL} />
          )
        }
      />
      <div className="h-flex">
        <div className="content">
          <div className="patient-profile h-flex">
            <div className="patient-data v-flex">
              <TitledPanel
                title={i18n["Page.PatientManagement.Profile.Title"]}
                withShadow={true}
                component={
                  patientLinks.length > 0 && (
                    <div className="vertical-dropdown">
                      <div className="vertical-dropdown-toggle">...</div>
                      <PatientLinks links={patientLinks} />
                    </div>
                  )
                }
              >
                <PatientCard
                  patient={props.patient.patientInformation}
                  patientData={getPatientLabelledData}
                  legalGuardians={props.patient.legalGuardians}
                  patientFullDetailsURL={props.patientFullDetailsURL}
                  invitationConfiguration={
                    props.accountCreationInvitationURL && {
                      currentDoctorUid: props.patient.attributes.doctorUid,
                      accountCreationInvitationURL: props.accountCreationInvitationURL,
                    }
                  }
                  convertToPatientURL={props.convertToPatientURL}
                  convertedPatientProfileURL={props.convertedPatientProfileURL}
                  showLeadStatus={props.showLeadStatus}
                />
              </TitledPanel>

              <Panel id="about" fullWidth={true} withShadow={true}>
                <AboutPanel
                  leadInformation={props.patient.patientInformation}
                  tagActions={props.tagActions}
                />
              </Panel>

              <Panel id="assignment" fullWidth={true} withShadow={true}>
                <AssignmentPanel
                  canEditDoctorAssignment={props.canEditDoctorAssignment}
                  canEditPracticeAssignment={props.canEditPracticeAssignment}
                  assignableDoctors={props.assignableDoctors}
                  assignablePractices={props.assignablePractices}
                  assignmentUrl={props.assignmentUrl}
                  isConverted={!!props.convertedPatientProfileURL}
                />
              </Panel>

              <TitledPanel
                id="last-reports"
                title={i18n["Page.PatientManagement.LastReports.Title"]}
                fullWidth={true}
                withShadow={true}
              >
                <ReportList {...props.reports} disableFiltering={true} />
              </TitledPanel>
            </div>
            <div className="activity-timeline-column">
              {props.reports.latestAssessmentResult && (
                <AssessmentResultPreview {...props.reports.latestAssessmentResult} />
              )}
              <ActivityTimeline
                activities={props.activities}
                actions={props.activityActions}
                patient={props.patient}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

PatientManagement.loadComponent = (elementId: string, props: PatientManagementProps) => {
  const container = document.getElementById(elementId);
  const root = createRoot(container);
  root.render(<PatientManagement {...props} />);
};
